/**
 * Workspace Selectors
 *
 * Atomic selectors for workspace state.
 * All selectors return primitives for minimal re-renders.
 *
 * @module store/selectors/workspaceSelectors
 */

import { DEFAULT_WORKSPACE_COUNTRY_CODE } from '../../constants/country';
import type { GeoMarkupStore } from '../index';
import type { MapSettings, WorkspaceListItem } from '../../types/workspace';

// ===== DATA STATE SELECTORS =====

/** Select extended workspace list with member counts */
export const selectExtendedWorkspaces = (state: GeoMarkupStore) =>
  state.workspace.extendedWorkspaces;

/** Select workspace loading state */
export const selectWorkspaceIsLoading = (state: GeoMarkupStore) =>
  state.workspace.isLoading;

/** Select workspace creating state */
export const selectWorkspaceIsCreating = (state: GeoMarkupStore) =>
  state.workspace.isCreating;

/** Select workspace error */
export const selectWorkspaceError = (state: GeoMarkupStore) =>
  state.workspace.error;

// ===== UI STATE SELECTORS =====

/** Select dashboard visibility */
export const selectIsDashboardOpen = (state: GeoMarkupStore) =>
  state.workspaceUI.dashboard.isOpen;

/** Select create modal visibility */
export const selectIsCreateModalOpen = (state: GeoMarkupStore) =>
  state.workspaceUI.createModal.isOpen;

/** Select edit modal visibility */
export const selectIsEditModalOpen = (state: GeoMarkupStore) =>
  state.workspaceUI.editModal.isOpen;

/** Select edit modal workspace ID */
export const selectEditModalWorkspaceId = (state: GeoMarkupStore) =>
  state.workspaceUI.editModal.workspaceId;

/** Select management panel visibility */
export const selectIsManagementPanelOpen = (state: GeoMarkupStore) =>
  state.workspaceUI.managementPanel.isOpen;

/** Select management panel workspace ID */
export const selectManagementPanelWorkspaceId = (state: GeoMarkupStore) =>
  state.workspaceUI.managementPanel.workspaceId;

/** Select management panel active tab */
export const selectManagementPanelActiveTab = (state: GeoMarkupStore) =>
  state.workspaceUI.managementPanel.activeTab;

/** Select invitation modal visibility */
export const selectIsInvitationModalOpen = (state: GeoMarkupStore) =>
  state.workspaceUI.invitationModal.isOpen;

/** Select invitation modal workspace ID */
export const selectInvitationModalWorkspaceId = (state: GeoMarkupStore) =>
  state.workspaceUI.invitationModal.workspaceId;

// ===== DERIVED SELECTORS =====

/** Check if user has any workspaces */
export const selectHasWorkspaces = (state: GeoMarkupStore) =>
  state.auth.workspaces.length > 0;

/** Get workspace count */
export const selectWorkspaceCount = (state: GeoMarkupStore) =>
  state.auth.workspaces.length;

/** Check if user needs workspace onboarding (authenticated but no workspaces) */
export const selectNeedsWorkspaceOnboarding = (state: GeoMarkupStore) =>
  state.auth.isAuthenticated &&
  !state.auth.isCheckingAuth &&
  state.auth.workspaces.length === 0;

// Cache for fallback workspace conversion to prevent infinite re-renders
let _fallbackCache: { sourceId: string; result: WorkspaceListItem } | null =
  null;

/** Get current managed workspace from extended list, with fallback to auth workspaces */
export function selectCurrentManagedWorkspace(
  state: GeoMarkupStore
): WorkspaceListItem | null {
  const managingId = state.workspaceUI.managementPanel.workspaceId;
  if (!managingId) return null;

  // Try extended workspaces first (has memberCount, createdAt)
  const extended = state.workspace.extendedWorkspaces.find(
    (workspace) => workspace.workspaceId === managingId
  );
  if (extended) return extended;

  // Fallback to basic workspaces from auth (converted to WorkspaceListItem)
  const basic = state.auth.workspaces.find(
    (workspace) => workspace.id === managingId
  );
  if (!basic) return null;

  // Return cached result if source workspace hasn't changed
  if (
    _fallbackCache &&
    _fallbackCache.sourceId === basic.id &&
    _fallbackCache.result.name === basic.name &&
    _fallbackCache.result.role === basic.role &&
    _fallbackCache.result.isOwner === basic.isOwner
  ) {
    return _fallbackCache.result;
  }

  const result: WorkspaceListItem = {
    workspaceId: basic.id,
    shortId: basic.shortId,
    name: basic.name,
    description: basic.description,
    isOwner: basic.isOwner,
    role: basic.role,
    memberCount: 0,
    autoStartTasksEnabled: false,
    // Map data intentionally left out of the _fallbackCache equality key —
    // config rarely changes and the lightweight auth fallback omits it.
    isMapConfigured: false,
    mapSettings: null,
    createdAt: '1970-01-01T00:00:00.000Z',
  };

  _fallbackCache = { sourceId: basic.id, result };
  return result;
}

/**
 * Per-workspace map settings for an explicit workspace, read from the extended
 * workspace list. Returns null when the workspace is unconfigured or its
 * extended data has not loaded yet. Use this when the target workspace is known
 * from the route (e.g. the map configurator) rather than the active one, to
 * avoid races with `currentWorkspaceId` syncing.
 */
export const selectWorkspaceMapSettings =
  (workspaceId: string | null) =>
  (state: GeoMarkupStore): MapSettings | null =>
    workspaceId
      ? (state.workspace.extendedWorkspaces.find(
          (workspace) => workspace.workspaceId === workspaceId
        )?.mapSettings ?? null)
      : null;

/**
 * Per-workspace map settings for the active workspace, read from the extended
 * workspace list. Returns null when the workspace is unconfigured or its
 * extended data has not loaded yet.
 */
export const selectActiveWorkspaceMapSettings = (
  state: GeoMarkupStore
): MapSettings | null =>
  selectWorkspaceMapSettings(state.auth.currentWorkspaceId)(state);

/**
 * Country code of the active workspace, used to gate country-scoped basemaps and
 * standard layers via `isFeatureInScope` (see `config/mapFeatures.ts`). Defaults
 * to PL when settings are absent so the existing PL-centric experience is
 * preserved until configuration loads.
 */
export const selectActiveWorkspaceCountryCode = (
  state: GeoMarkupStore
): string =>
  selectActiveWorkspaceMapSettings(state)?.countryCode ??
  DEFAULT_WORKSPACE_COUNTRY_CODE;

/** Check if any workspace UI panel is open */
export const selectIsAnyWorkspacePanelOpen = (state: GeoMarkupStore) =>
  state.workspaceUI.dashboard.isOpen ||
  state.workspaceUI.createModal.isOpen ||
  state.workspaceUI.editModal.isOpen ||
  state.workspaceUI.managementPanel.isOpen ||
  state.workspaceUI.invitationModal.isOpen;
